import { useEffect, useMemo } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { isHttpClientErrorResponse } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getProjectKeyId } from '@atlassian/jira-router-resources-utils/src/utils/get-project-key-id/index.tsx';
import type {
	IrremovableField,
	IrremovableFieldsConfig,
} from '@atlassian/jira-jql-builder-basic/src/utils/irremovable-fields/index.tsx';
import {
	createResource,
	useResource,
	type RouterContext,
	type Match,
	type Query,
} from '@atlassian/react-resource-router';

import {
	TEAM_NAME,
	PACKAGE_NAME,
	SERVICE_DESK_ORIGIN_DUPLICATE_FILTER,
	SERVICE_DESK_ORIGIN_FILTER,
	REQUEST_TYPE_FILTER,
} from '../../common/constants.tsx';
import type {
	FilterFieldResponse,
	FilterFieldResponseData,
	FiltersProps,
	FetchFilterFieldsResult,
	RequestTypeFieldResponse,
} from './types.tsx';
import { QUERY, QUERY_OLD } from './gql.tsx';

const RESOURCE_NAME = 'projectSummaryFilterFieldResource';
export const RESOURCE_TYPE = 'RESOURCE_TYPE_JSM_PROJECT_SUMMARY_FILTERS';
const REQUEST_TYPE_FILTER_TYPE = 'com.atlassian.servicedesk:vp-origin';
const REQUEST_TYPE_FILTER_SEARCH_TEMPLATE = 'com.atlassian.servicedesk:vp-origin-searcher';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const createdField = {
	displayName: 'Created',
	fieldType: 'created',
	jqlTerm: 'created',
	operators: [
		'LESS_THAN_OR_EQUAL',
		'GREATER_THAN_OR_EQUAL',
		'NOT_IN',
		'IS_NOT',
		'LESS_THAN',
		'GREATER_THAN',
		'EQUALS',
		'IS',
		'IN',
		'NOT_EQUALS',
	],
	searchTemplate: 'created',
	type: 'created',
} as unknown as IrremovableField;

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const priorityField = {
	displayName: 'Priority',
	fieldType: 'priority',
	jqlTerm: 'priority',
	operators: [
		'GREATER_THAN_OR_EQUAL',
		'LESS_THAN',
		'GREATER_THAN',
		'WAS_NOT',
		'IS',
		'IN',
		'CHANGED',
		'LESS_THAN_OR_EQUAL',
		'NOT_IN',
		'IS_NOT',
		'WAS_NOT_IN',
		'EQUALS',
		'WAS_IN',
		'WAS',
		'NOT_EQUALS',
	],
	searchTemplate: 'priority',
	type: 'priority',
} as unknown as IrremovableField;

const reportError = ({ error, id }: { error: Error; id?: string }) =>
	fireErrorAnalytics({
		error,
		sendToPrivacyUnsafeSplunk: true,
		meta: {
			id: id ? `${RESOURCE_NAME}.${id}` : RESOURCE_NAME,
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
		},
	});

const fetchFilterFieldsOld = async (
	cloudId: string,
	jqlContext: string,
): Promise<FetchFilterFieldsResult> => {
	try {
		const response = await performPostRequest<FilterFieldResponse>('/gateway/api/graphql', {
			body: JSON.stringify({
				query: QUERY_OLD,
				variables: {
					cloudId,
					contextFieldsFilter: {
						jqlTerms: [
							REQUEST_TYPE_FILTER,
							SERVICE_DESK_ORIGIN_FILTER,
							SERVICE_DESK_ORIGIN_DUPLICATE_FILTER,
						],
					},
					jqlContext,
				},
			}),
			headers: {
				'content-type': 'application/json',
				'X-ExperimentalApi': 'JiraJqlBuilder',
			},
		});

		const tryGetField = ({
			data,
			type,
			searchTemplate,
			displayName,
		}: {
			data: FilterFieldResponseData;
			type: string;
			searchTemplate: string;
			displayName?: string;
		}) => {
			const edges = data?.jira?.jqlBuilder?.fields?.edges;

			const field = edges?.find(
				(edge) => edge?.node?.type === type && edge?.node?.searchTemplate?.key === searchTemplate,
			)?.node;

			return field
				? {
						...field,
						searchTemplate: field.searchTemplate.key,
						fieldType: field.type,
						displayName: displayName ?? field.displayName,
					}
				: undefined;
		};

		return {
			requestTypeField: tryGetField({
				data: response.data,
				type: REQUEST_TYPE_FILTER_TYPE,
				searchTemplate: REQUEST_TYPE_FILTER_SEARCH_TEMPLATE,
			}),
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (!isHttpClientErrorResponse(error)) {
			reportError({
				error,
				id: 'fetchFields',
			});
		}

		return {
			requestTypeField: undefined,
		};
	}
};

const fetchFilterFields = async (cloudId: string): Promise<FetchFilterFieldsResult> => {
	try {
		const requestTypeFieldResponse = await performPostRequest<RequestTypeFieldResponse>(
			'/gateway/api/graphql',
			{
				body: JSON.stringify({
					query: QUERY,
					variables: {
						cloudId,
					},
				}),
				headers: {
					'content-type': 'application/json',
				},
			},
		);

		const requestTypeField = requestTypeFieldResponse.data?.jira?.requestTypeField;
		if (!requestTypeField) {
			return {
				requestTypeField: undefined,
			};
		}

		return {
			requestTypeField: {
				...requestTypeField,
				searchTemplate: requestTypeField.searchTemplate?.key,
				fieldType: requestTypeField.type,
				displayName: requestTypeField.displayName,
			},
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (!isHttpClientErrorResponse(error)) {
			reportError({
				error,
				id: 'fetchFields',
			});
		}

		return {
			requestTypeField: undefined,
		};
	}
};

const getProjectKeyOrId = (match: Match, query: Query) => {
	const { projectKey, projectId } = getProjectKeyId(match, query);
	return projectKey || projectId;
};

const filterFieldsResource = createResource({
	type: RESOURCE_TYPE,
	getKey: ({ match, query }: RouterContext) => RESOURCE_TYPE + getProjectKeyOrId(match, query),
	getData: ({ match, query }, { tenantContext: { cloudId } }) => {
		const jqlContext = `project = '${getProjectKeyOrId(match, query)}'`;
		return fg('move_jsm_views_rt_filter_to_new_query')
			? fetchFilterFields(cloudId)
			: fetchFilterFieldsOld(cloudId, jqlContext);
	},
	maxAge: Infinity,
});

export const getProjectSummaryFilterFieldsResource = () => filterFieldsResource;

export const useGetProjectSummaryFilterFields = () => {
	const { data: filterResource } = useResource(filterFieldsResource);

	const useFilterFields = useMemo(
		() =>
			({
				requestTypeField = filterResource?.requestTypeField,
				requestTypeDisplayName,
				priorityDisplayName,
				createdDisplayName,
			}: FiltersProps): IrremovableFieldsConfig => {
				return [
					...(requestTypeField
						? [
								{
									...requestTypeField,
									...(requestTypeDisplayName && { displayName: requestTypeDisplayName }),
								},
							]
						: []),
					'status',
					'assignee',
					{
						...priorityField,
						...(priorityDisplayName && { displayName: priorityDisplayName }),
					},
					{ ...createdField, ...(createdDisplayName && { displayName: createdDisplayName }) },
				];
			},
		[filterResource],
	);

	return useFilterFields;
};

export const useRequestTypeJqlTerm = ({ jqlTerm }: { jqlTerm?: string }) => {
	const { data: filterResource, loading } = useResource(filterFieldsResource);

	const term = jqlTerm ?? filterResource?.requestTypeField?.jqlTerm;
	const hasError = !loading && !term;

	useEffect(() => {
		if (hasError) {
			reportError({
				error: new Error('Error while getting request type jql term.'),
				id: 'rtJqlTerm',
			});
		}
	}, [hasError]);

	return term;
};
